import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, StatCard } from '../../components/UI'
import { get } from '../../utils/api'
import { formatCurrency, formatDate, timeAgo } from '../../utils/helpers'
import { IconBuildingSkyscraper, IconUsers, IconCurrencyDollar, IconTrendingUp, IconTarget, IconCalendarEvent } from '@tabler/icons-react'

export default function AdminDashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    get('/admin/stats').then(setStats).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading || !stats) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Platform Dashboard" subtitle="Leadiox SaaS Overview" />

      {/* Key Metrics */}
      <div className="row row-deck row-cards mb-4">
        <div className="col-sm-6 col-lg-3">
          <StatCard label="Total Hotels" value={stats.hotels.total} change={`${stats.hotels.active} active`} icon={IconBuildingSkyscraper} color="primary" />
        </div>
        <div className="col-sm-6 col-lg-3">
          <StatCard label="Total Users" value={stats.users.total} change={`${stats.users.active} active`} icon={IconUsers} color="purple" />
        </div>
        <div className="col-sm-6 col-lg-3">
          <StatCard label="MRR" value={formatCurrency(stats.mrr)} icon={IconCurrencyDollar} color="green" />
        </div>
        <div className="col-sm-6 col-lg-3">
          <StatCard label="ARR" value={formatCurrency(stats.mrr * 12)} icon={IconTrendingUp} color="green" />
        </div>
      </div>

      {/* Platform Activity */}
      <div className="row row-deck row-cards mb-4">
        <div className="col-sm-6 col-lg-4">
          <StatCard label="Total Leads (All Hotels)" value={stats.leads} icon={IconTarget} color="blue" />
        </div>
        <div className="col-sm-6 col-lg-4">
          <StatCard label="Open Deals" value={stats.deals.total} change={`${formatCurrency(stats.deals.pipeline)} pipeline`} icon={IconCurrencyDollar} color="purple" />
        </div>
        <div className="col-sm-6 col-lg-4">
          <StatCard label="Total Events" value={stats.events} icon={IconCalendarEvent} color="primary" />
        </div>
      </div>

      {/* Plans & Status Breakdown */}
      <div className="row row-deck row-cards mb-4">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Hotels by Plan</h3></div>
            <div className="card-body">
              {stats.plans.map(p => {
                const colors = { trial: 'secondary', starter: 'blue', professional: 'purple', enterprise: 'green' }
                return (
                  <div key={p.plan} className="d-flex align-items-center mb-3">
                    <span className={`badge bg-${colors[p.plan] || 'secondary'}-lt me-3`} style={{ minWidth: 100 }}>
                      {(p.plan || 'unknown').charAt(0).toUpperCase() + (p.plan || '').slice(1)}
                    </span>
                    <div className="flex-fill">
                      <div className="progress" style={{ height: 8 }}>
                        <div className={`progress-bar bg-${colors[p.plan] || 'secondary'}`}
                          style={{ width: `${(parseInt(p.count) / stats.hotels.total) * 100}%` }} />
                      </div>
                    </div>
                    <span className="ms-3 fw-bold">{p.count}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Subscription Status</h3></div>
            <div className="card-body">
              {stats.statuses.map(s => {
                const colors = { active: 'green', trial: 'blue', past_due: 'orange', cancelled: 'red', suspended: 'red' }
                return (
                  <div key={s.subscription_status} className="d-flex align-items-center mb-3">
                    <span className={`badge bg-${colors[s.subscription_status] || 'secondary'}-lt me-3`} style={{ minWidth: 100 }}>
                      {(s.subscription_status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                    <div className="flex-fill">
                      <div className="progress" style={{ height: 8 }}>
                        <div className={`progress-bar bg-${colors[s.subscription_status] || 'secondary'}`}
                          style={{ width: `${(parseInt(s.count) / stats.hotels.total) * 100}%` }} />
                      </div>
                    </div>
                    <span className="ms-3 fw-bold">{s.count}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Hotels & Users */}
      <div className="row row-deck row-cards">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Hotels</h3>
              <div className="card-actions"><a href="/admin/hotels" className="btn btn-primary btn-sm">View All</a></div>
            </div>
            <div className="list-group list-group-flush">
              {stats.recentHotels.map(h => (
                <div key={h.id} className="list-group-item list-group-item-action" onClick={() => navigate('/admin/hotels')} style={{ cursor: 'pointer' }}>
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className="avatar avatar-sm rounded bg-primary-lt"><IconBuildingSkyscraper size={18} /></span>
                    </div>
                    <div className="col">
                      <div className="fw-medium">{h.name}</div>
                      <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{h.city}, {h.state}</div>
                    </div>
                    <div className="col-auto">
                      <span className={`badge bg-${h.subscription_status === 'active' ? 'green' : 'yellow'}-lt`}>{h.plan}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Users</h3>
              <div className="card-actions"><a href="/admin/users" className="btn btn-primary btn-sm">View All</a></div>
            </div>
            <div className="list-group list-group-flush">
              {stats.recentUsers.map(u => (
                <div key={u.id} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className="avatar avatar-sm rounded-circle bg-purple-lt">
                        {u.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div className="col">
                      <div className="fw-medium">{u.name}</div>
                      <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{u.hotel_name} — {u.role?.replace(/_/g, ' ')}</div>
                    </div>
                    <div className="col-auto text-secondary" style={{ fontSize: '0.75rem' }}>
                      {u.last_login ? timeAgo(u.last_login) : 'Never'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
